import { z } from "zod";

export const intakeFormSchema = z.object({
  // Personal Information
  first_name: z.string().min(1, "First name is required").max(100),
  last_name: z.string().min(1, "Last name is required").max(100),
  date_of_birth: z.string().min(1, "Date of birth is required"),
  ssn_last_four: z.string().max(4).optional(),
  phone: z.string().min(10, "Valid phone number required").max(15),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
  address_line1: z.string().min(1, "Address is required").max(200),
  address_line2: z.string().max(200).optional(),
  city: z.string().min(1, "City is required").max(100),
  state: z.string().min(2, "State is required").max(2),
  zip_code: z.string().min(5, "ZIP code is required").max(10),

  // Insurance Information
  insurance_type: z.enum(["medicare", "medicaid", "dual_eligible"], {
    required_error: "Please select insurance type",
  }),
  medicare_id: z.string().max(20).optional(),
  medicaid_id: z.string().max(20).optional(),
  medicare_part_a: z.boolean().default(false),
  medicare_part_b: z.boolean().default(false),
  medicare_part_c: z.boolean().default(false),
  medicare_part_d: z.boolean().default(false),
  group_number: z.string().max(50).optional(),
  plan_type: z.string().max(100).optional(),
  payer_name: z.string().max(200).optional(),
  claims_address: z.string().max(500).optional(),
  payer_phone: z.string().max(20).optional(),
  edi_payer_id: z.string().max(50).optional(),
  rx_bin: z.string().max(20).optional(),
  rx_pcn: z.string().max(20).optional(),
  rx_group: z.string().max(50).optional(),
  rx_id: z.string().max(50).optional(),
  effective_date: z.string().max(20).optional(),
  copay_amount: z.string().max(100).optional(),
  deductible_info: z.string().max(200).optional(),
  network_tier: z.string().max(50).optional(),

  // Primary Care
  primary_care_physician: z.string().max(100).optional(),
  pcp_phone: z.string().max(15).optional(),

  // Emergency Contact
  emergency_contact_name: z.string().min(1, "Emergency contact name is required").max(100),
  emergency_contact_phone: z.string().min(10, "Valid phone number required").max(15),
  emergency_contact_relationship: z.string().min(1, "Relationship is required").max(50),

  // Medical History
  current_medications: z.string().max(1000).optional(),
  allergies: z.string().max(500).optional(),
  chronic_conditions: z.string().max(500).optional(),

  // Accessibility Devices & Tools
  mobility_aids: z.array(z.string()).default([]),
  hearing_devices: z.array(z.string()).default([]),
  vision_devices: z.array(z.string()).default([]),
  communication_devices: z.array(z.string()).default([]),
  other_assistive_devices: z.string().max(500).optional(),
  accessibility_needs: z.string().max(1000).optional(),

  // Social & Lifestyle
  hobbies: z.array(z.string()).default([]),
  social_activities: z.array(z.string()).default([]),
  living_situation: z.string().max(50).optional(),
  social_support_level: z.string().max(50).optional(),
  preferred_communication_style: z.string().max(50).optional(),
  interests_notes: z.string().max(1000).optional(),

  // SDOH Screening
  sdoh_screenings: z.array(z.string()).default([]),
  sdoh_notes: z.string().max(1000).optional(),

  // Photo Uploads
  patient_photo_url: z.string().optional(),
  insurance_card_front_url: z.string().optional(),
  insurance_card_back_url: z.string().optional(),

  // Consent
  consent_given: z.boolean().refine((val) => val === true, {
    message: "You must agree to the terms to submit this form",
  }),
  signature: z.string().min(1, "Signature is required").max(100),
});

export type IntakeFormData = z.infer<typeof intakeFormSchema>;

export const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];
